/**
 * Shared formatting utilities.
 */

import { format, isValid, parseISO } from "date-fns";

const TZ = "America/New_York";

export function formatDateTimeET(value: string | Date): string {
  return new Date(value).toLocaleString("en-US", {
    timeZone: TZ,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

export function formatDateET(value: string | Date): string {
  return new Date(value).toLocaleDateString("en-US", {
    timeZone: TZ,
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatTimeET(value: string | Date): string {
  return new Date(value).toLocaleTimeString("en-US", {
    timeZone: TZ,
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const RELATIVE_THRESHOLD_DAYS = 30;

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

export function formatRelativeTime(value: string): string {
  const diffSec = (new Date(value).getTime() - Date.now()) / 1000;
  const absSec = Math.abs(diffSec);

  if (absSec < MINUTE) return "just now";
  if (absSec < HOUR) return rtf.format(Math.round(diffSec / MINUTE), "minute");
  if (absSec < DAY) return rtf.format(Math.round(diffSec / HOUR), "hour");
  return rtf.format(Math.round(diffSec / DAY), "day");
}

export function formatSmartDateTime(value: string): {
  display: string;
  full: string;
  isRelative: boolean;
} {
  const full = formatDateTimeET(value);
  const absDays = Math.abs(new Date(value).getTime() - Date.now()) / (1000 * DAY);

  if (absDays <= RELATIVE_THRESHOLD_DAYS) {
    return { display: formatRelativeTime(value), full, isRelative: true };
  }

  return { display: formatDateET(value), full, isRelative: false };
}

/**
 * `formatDateToUSAStandard` (`utils/common.ts`): `parseISO` then `M/d/yy`, no
 * time-zone option — the wire's string read as the source reads its own.
 */
export function formatShortUsDate(value: string): string {
  const date = parseISO(value);
  return isValid(date) ? format(date, "M/d/yy") : "";
}

/**
 * The Integrations card's Checked row (#747): the source's `formatCheckedAt`
 * (`ConsumedApis.tsx:26-37`) verbatim — `en-US`, short month, no year, a
 * 12-hour time with seconds, in the **viewer's** zone (no `timeZone` option), and
 * a string it cannot read returned as given. The read runs in the browser after
 * mount, so the server never renders this in its own zone.
 */
export function formatCheckedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}
